import { PredefinedAreaRole } from '../types/roles';

export interface UserNameDTO {
  firstName: string;
  lastName: string;
}

export interface AccountInfoDTO {
  accountImage?: string;
  accountName?: string;
}

export interface CurrentUserDTO {
  first: string;
  last: string;
  profileImage: string;
}

export interface UserDTO {
  id: string;
  roles: string[];
  email: string;
  name?: UserNameDTO;
  profileImage: string;
  joinedTeamAt: string;
  isPendingOwner?: boolean;
  policyIds: string[];
}

export interface InviteDTO {
  id: string;
  role: string;
  email: string;
  status: InviteStatusType;
  acceptLink: string;
  staffId: string;
  dateCreated: string;
  isPendingOwner?: boolean;
  policyIds: string[];
}

export interface TeamDTO {
  accountId: string;
  accountInfo: AccountInfoDTO;
  policyIds: string[];
}

export interface SendInvitesRequestDTO {
  accountId: string;
  policyIds?: string[];
  emails: string[];
}

export interface SendInvitesResponseDTO {
  invites: InviteDTO[];
  failedEmails: string[];
}

export interface SetTeamDetailsResponseDTO {
  accountId: string;
  accountImg: string;
  accountName: string;
  accountOwner: string;
  slug: string;
  status: InviteStatusType;
}

export interface TeamMembersDTO {
  users: UserDTO[];
  invites: InviteDTO[];
  teams?: TeamDTO[];
  accountInfo: AccountInfoDTO;
  targetAccountId: string;
  userId: string;
  permissions?: string[];
  policies: RoleDTO[];
  predefinedRoles: PredefinedRolesDTO;
  loggedInAccountId?: string;
  pendingOwner?: PendingOwnerDTO;
  totalUsersInAccount?: string;
  contributorLimit?: {
    contributorLimit: number
  };
}

export interface UsersMembersDTO {
  users: UserDTO[];
}

export interface PendingOwnerDTO {
  email: string;
  expirationDate: string;
  acceptLink: string;
}

export interface RolesDTO {
  roles: RoleDTO[];
}

export interface RoleDTO {
  id: string;
  name?: string;
  description?: string;
  isCustom: boolean;
  scopes: string[];
  experiments?: string[];
  deprecated?: boolean;
}

export interface PredefinedRolesDTO {
  roles: PredefinedAreaRole[];
}

export enum RoleKey {
  OWNER = 'owner',
  ADMIN = 'admin',
  LIMITED_ADMIN = 'limitedAdmin',
  BACKOFFICE_MANAGER = 'backOfficeManager',
  WEBSITE_MANAGER = 'websiteManager',
  LIMITED_WEBSITE_MANAGER = 'limitedWebsiteManager',
  BOOKINGS_ADMIN = 'bookingsAdmin',
  ACCOUNT_LIMITED_WEBSITE_MANAGER = 'accountLimitedWebsiteManager',
  ACCOUNT_WEBSITE_MANAGER = 'accountWebsiteManager',
  ACCOUNT_ADMIN = 'accountAdmin',
  ACCOUNT_LIMITED_ADMIN = 'accountLimitedAdmin',
}

export enum InviteStatusType {
  PENDING = 'Pending',
  EXPIRED = 'Expired',
  USED = 'Used',
  DELETED = 'Deleted',
  DECLINED = 'Declined'
}

export enum PermissionKey {
  SEND_INVITE = 'SEND_INVITE',
  CHANGE_INVITE = 'CHANGE_INVITE',
  DELETE_INVITE = 'DELETE_INVITE',
  ACCEPT_INVITE = 'ACCEPT_INVITE',
  VIEW_INVITES = 'VIEW_INVITES',
  CHANGE_MEMBER = 'CHANGE_MEMBER',
  DELETE_MEMBER = 'DELETE_MEMBER',
  UPDATE_PROFILE = 'UPDATE_PROFILE',
  VIEW_TEAM = 'VIEW_TEAM',
  DELETE_SELF = 'DELETE_SELF',
  MANAGE_ROLES = 'MANAGE_ROLES'
}

export const AccountPermissionType = {
  [PermissionKey.SEND_INVITE]: 'team.send-invite',
  [PermissionKey.CHANGE_INVITE]: 'team.change-invite',
  [PermissionKey.DELETE_INVITE]: 'team.delete-invite',
  [PermissionKey.ACCEPT_INVITE]: 'team.accept-invite',
  [PermissionKey.VIEW_INVITES]: 'team.view-invites',
  [PermissionKey.CHANGE_MEMBER]: 'team.change-member',
  [PermissionKey.DELETE_MEMBER]: 'team.delete-member',
  [PermissionKey.UPDATE_PROFILE]: 'team.update-profile',
  [PermissionKey.VIEW_TEAM]: 'team.view-team',
  [PermissionKey.DELETE_SELF]: 'team.delete-self',
  [PermissionKey.MANAGE_ROLES]: 'team.manage-team-member-roles',
};

export const SitePermissionType = {
  [PermissionKey.SEND_INVITE]: 'site-users.send-invite',
  [PermissionKey.CHANGE_INVITE]: 'site-users.send-invite',
  [PermissionKey.DELETE_INVITE]: 'site-users.delete-invite',
  [PermissionKey.VIEW_INVITES]: 'site-users.view-invitations',
  [PermissionKey.CHANGE_MEMBER]: 'REPLACE_CONTRIBUTOR_ROLES',
  [PermissionKey.DELETE_MEMBER]: 'site-users.remove-user',
  [PermissionKey.VIEW_TEAM]: 'site-users.view-users',
  [PermissionKey.DELETE_SELF]: 'site-users.remove-self',
  [PermissionKey.MANAGE_ROLES]: 'team.manage-team-member-roles',
};

export type IsAppInstalled = (s: string) => boolean;
